import {
  getSlopeCheckCache,
  getSunPositions,
  invalidateSunPositionCache,
  recomputeParking,
  recomputeSlopeCheck,
  scheduleSave,
  state,
} from '../app';
import { getBuilding, normalizeAngle } from '../buildingInteraction';
import { redrawBuildingsOverlay } from '../buildingRenderer';
import {
  DEFAULT_FLOOR_HEIGHT_M,
  getBuildingHeight,
  polygonCentroid,
  toDeg,
  toRad,
} from '../geometry';
import { computeLegalCheck } from '../legalCheck';
import {
  AISLE_WIDTH_FIXED_M,
  computeParkingTarget,
  EXTRA_PARKING_METHODS_ENABLED,
  PARKING_METHOD_COLORS,
  PARKING_METHOD_LABELS,
  PARKING_TARGET_RATIOS,
} from '../parking/constants';
import { PARKING_ANGLE_OFFSETS_DEG } from '../parkingEngine';
import { MIN_SUN_ALTITUDE_DEG, shadowColorForHour } from '../shadowCheck';
import { drawLockedSitePolygon, rebuildDrivewayMarkers, totalDrivewayCount } from '../siteEditor';
import { applyZoningPreset, ZONING_DISTRICT_PRESETS } from '../slopeCheck';
import type { Building, Driveway } from '../types';
import { el, makeCollapsible, numToAlpha, setBadge } from './el';

export function nextBuildingName(): string {
  const used = new Set(state.buildings.map((b) => b.name));
  let n = 1;
  while (used.has(`${numToAlpha(n)}棟`)) n++;
  return `${numToAlpha(n)}棟`;
}
export function addBuilding(): void {
  if (!state.site.origin) {
    alert('先に敷地を指定してください。');
    return;
  }
  const centroid =
    state.site.points.length >= 3 ? polygonCentroid(state.site.points) : { x: 0, y: 0 };
  const idx = state.buildings.length;
  const center = idx === 0 ? { ...centroid } : { x: centroid.x + idx * 25, y: centroid.y };
  const id = `b${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  state.buildings.push({
    id,
    name: nextBuildingName(),
    center,
    width: 20,
    depth: 15,
    angle: 0,
    floors: 8,
    floorHeightM: DEFAULT_FLOOR_HEIGHT_M,
  });
  state.ui.selectedId = id;
  recomputeSlopeCheck();
  recomputeParking();
  redrawBuildingsOverlay();
  renderSidebar();
  scheduleSave();
}
export function removeBuilding(id: string): void {
  state.buildings = state.buildings.filter((b) => b.id !== id);
  if (state.ui.selectedId === id) state.ui.selectedId = null;
  recomputeSlopeCheck();
  recomputeParking();
  redrawBuildingsOverlay();
  renderSidebar();
  scheduleSave();
}
export function selectBuilding(id: string): void {
  state.ui.selectedId = id;
  redrawBuildingsOverlay();
  highlightSelectedRow();
}
export function highlightSelectedRow(): void {
  // ②の表は棟が列になっているため、行(tr)ではなく各棟の列に属するセル(td[data-id])に選択状態を反映する
  document.querySelectorAll('#building-table td[data-id]').forEach((td) => {
    td.classList.toggle('selected', td.getAttribute('data-id') === state.ui.selectedId);
  });
}
export function updateLegalSummary(): void {
  const r = computeLegalCheck(state);
  const set = (id: string, val: string) => {
    const n = document.getElementById(id);
    if (n) n.textContent = val;
  };
  set('sum-site-area', r.siteArea.toFixed(1));
  set('sum-footprint', r.totalFootprint.toFixed(1));
  set('sum-bcr', r.bcr.toFixed(1));
  set('sum-gfa', r.totalGfa.toFixed(1));
  set('sum-far', r.far.toFixed(1));
  const bcrBadge = document.getElementById('badge-bcr');
  const farBadge = document.getElementById('badge-far');
  if (bcrBadge) setBadge(bcrBadge, r.bcrOk);
  if (farBadge) setBadge(farBadge, r.farOk);
}

export function renderSidebar(): void {
  recomputeSlopeCheck();
  recomputeParking();
  const sidebar = document.getElementById('sidebar');
  if (!sidebar) return;
  sidebar.innerHTML = '';
  const r = computeLegalCheck(state);
  const slopeCheckCache = getSlopeCheckCache();

  /* ① 敷地カード */
  const edgeRows = state.site.locked
    ? (state.site.edges || []).flatMap((edge, i) => {
        if (!Array.isArray(edge.driveways)) edge.driveways = [];
        const p1 = state.site.points[i];
        const p2 = state.site.points[(i + 1) % state.site.points.length];
        const edgeLenM = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const len = edgeLenM.toFixed(1);
        const typeSelect = el(
          'select',
          {
            onchange: (e: Event) => {
              const value = (e.target as HTMLSelectElement).value as typeof edge.type;
              edge.type = value;
              if (edge.type !== 'road') {
                edge.roadWidthM = null;
                edge.driveways = [];
              } else {
                if (edge.roadWidthM == null) edge.roadWidthM = 6;
                if (totalDrivewayCount() === 0) {
                  edge.driveways.push({
                    id: `dw${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                    position: 0.5,
                    widthM: 6,
                    angleDeg: 0,
                  });
                }
              }
              drawLockedSitePolygon();
              recomputeSlopeCheck();
              recomputeParking();
              redrawBuildingsOverlay();
              renderSidebar();
              scheduleSave();
            },
          },
          [
            el(
              'option',
              { value: 'none', ...(edge.type === 'none' ? { selected: 'selected' } : {}) },
              'なし'
            ),
            el(
              'option',
              { value: 'road', ...(edge.type === 'road' ? { selected: 'selected' } : {}) },
              '道路'
            ),
            el(
              'option',
              { value: 'neighbor', ...(edge.type === 'neighbor' ? { selected: 'selected' } : {}) },
              '隣地'
            ),
          ]
        );
        const roadWidthCell =
          edge.type === 'road'
            ? el('div', {}, [
                el('input', {
                  type: 'number',
                  value: edge.roadWidthM || 6,
                  step: '0.5',
                  min: '2',
                  style: { width: '54px' },
                  onchange: (e: Event) => {
                    edge.roadWidthM = Math.max(
                      2,
                      Number.parseFloat((e.target as HTMLInputElement).value) || 6
                    );
                    recomputeSlopeCheck();
                    recomputeParking();
                    redrawBuildingsOverlay();
                    renderSidebar();
                    scheduleSave();
                  },
                }),
                el(
                  'button',
                  {
                    class: 'btn small',
                    style: { marginTop: '2px' },
                    onclick: () => {
                      edge.driveways.push({
                        id: `dw${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                        position: 0.5,
                        widthM: 6,
                        angleDeg: 0,
                      });
                      rebuildDrivewayMarkers();
                      renderSidebar();
                      redrawBuildingsOverlay();
                      scheduleSave();
                    },
                  },
                  '+出入口'
                ),
              ])
            : el('span', {}, '—');
        const mainRow = el('tr', {}, [
          el('td', {}, `${i + 1}`),
          el('td', {}, `${len}m`),
          el('td', {}, typeSelect),
          el('td', {}, roadWidthCell),
        ]);
        const rows = [mainRow];
        if (edge.type === 'road' && edge.driveways.length > 0) {
          const dwRows = edge.driveways.map((dw: Driveway) => {
            const posM = (dw.position * edgeLenM).toFixed(1);
            return el('tr', {}, [
              el(
                'td',
                {},
                el('input', {
                  type: 'number',
                  value: posM,
                  step: '0.5',
                  min: '0',
                  max: len,
                  style: { width: '50px' },
                  onchange: (e: Event) => {
                    const distM = Math.max(
                      0,
                      Math.min(
                        edgeLenM,
                        Number.parseFloat((e.target as HTMLInputElement).value) || 0
                      )
                    );
                    dw.position = edgeLenM > 0 ? distM / edgeLenM : 0;
                    rebuildDrivewayMarkers();
                    recomputeParking();
                    renderSidebar();
                    redrawBuildingsOverlay();
                    scheduleSave();
                  },
                })
              ),
              el(
                'td',
                {},
                el('input', {
                  type: 'number',
                  value: dw.widthM,
                  step: '0.5',
                  min: '2',
                  style: { width: '50px' },
                  onchange: (e: Event) => {
                    dw.widthM = Math.max(
                      2,
                      Number.parseFloat((e.target as HTMLInputElement).value) || 6
                    );
                    rebuildDrivewayMarkers();
                    recomputeParking();
                    renderSidebar();
                    redrawBuildingsOverlay();
                    scheduleSave();
                  },
                })
              ),
              el(
                'td',
                {},
                el('input', {
                  type: 'number',
                  value: dw.angleDeg ?? 0,
                  step: '5',
                  min: '-60',
                  max: '60',
                  title:
                    '道路に対して垂直(0°)からの傾き。正負で敷地内側への進入方向が左右に傾きます。',
                  style: { width: '50px' },
                  onchange: (e: Event) => {
                    dw.angleDeg = Math.max(
                      -60,
                      Math.min(60, Number.parseFloat((e.target as HTMLInputElement).value) || 0)
                    );
                    recomputeParking();
                    renderSidebar();
                    redrawBuildingsOverlay();
                    scheduleSave();
                  },
                })
              ),
              el(
                'td',
                {},
                el('input', {
                  type: 'number',
                  value: dw.lengthM ?? '',
                  placeholder: '自動',
                  step: '0.5',
                  min: '1',
                  title:
                    '出入口から敷地内側へ伸びる通路の長さ。空欄なら駐車レーンに届くまで自動で伸ばします。',
                  style: { width: '50px' },
                  onchange: (e: Event) => {
                    const v = Number.parseFloat((e.target as HTMLInputElement).value);
                    dw.lengthM = Number.isFinite(v) && v > 0 ? Math.max(1, v) : null;
                    recomputeParking();
                    renderSidebar();
                    redrawBuildingsOverlay();
                    scheduleSave();
                  },
                })
              ),
              el(
                'td',
                {},
                totalDrivewayCount() <= 1
                  ? el(
                      'button',
                      {
                        class: 'btn small danger',
                        disabled: 'disabled',
                        title: '出入口は最低1か所必要です',
                      },
                      '✕'
                    )
                  : el(
                      'button',
                      {
                        class: 'btn small danger',
                        onclick: () => {
                          edge.driveways = edge.driveways.filter((x) => x.id !== dw.id);
                          rebuildDrivewayMarkers();
                          recomputeParking();
                          redrawBuildingsOverlay();
                          renderSidebar();
                          scheduleSave();
                        },
                      },
                      '✕'
                    )
              ),
            ]);
          });
          rows.push(
            el('tr', {}, [
              el('td', { colspan: '4', style: { padding: '2px 4px 6px 16px' } }, [
                el('table', { class: 'tbl' }, [
                  el(
                    'thead',
                    {},
                    el(
                      'tr',
                      {},
                      ['位置(m)', '幅(m)', '角度(°)', '長さ(m)', ''].map((h) => el('th', {}, h))
                    )
                  ),
                  el('tbody', {}, dwRows),
                ]),
              ]),
            ])
          );
        }
        return rows;
      })
    : [];
  const siteCard = el('section', { class: 'card' }, [
    el('h2', {}, '① 敷地'),
    state.site.locked
      ? el('div', {}, [
          el('div', { class: 'kpi-line' }, [
            el('span', {}, '敷地面積'),
            el('b', {}, `${r.siteArea.toFixed(1)} m²`),
          ]),
          el('div', { class: 'kpi-line' }, [
            el('span', {}, '頂点数'),
            el('b', {}, String(state.site.points.length)),
          ]),
          el('label', {}, '辺の属性(道路斜線・隣地斜線の判定に使用)'),
          el('table', { class: 'tbl' }, [
            el(
              'thead',
              {},
              el(
                'tr',
                {},
                ['辺', '長さ', '種別', '道路幅員(m)'].map((h) => el('th', {}, h))
              )
            ),
            el('tbody', {}, edgeRows),
          ]),
          el(
            'div',
            { class: 'hint' },
            '道路の辺は黄色、隣地の辺は緑の破線で地図上に表示されます。各辺の中点にはこの表の「辺」番号と対応する番号ラベルが表示されます。'
          ),
          state.site.edges.some((e) => e.type === 'road')
            ? el(
                'div',
                { class: 'hint' },
                '出入口は道路の辺にのみ設定できます。最低1か所必要です(道路の辺を設定すると自動的に1か所追加されます)。出入口の位置には駐車場の通路領域が自動生成されます。'
              )
            : el(
                'div',
                { class: 'hint' },
                '出入口を設定するには、いずれかの辺を「道路」に設定してください(自動的に出入口が1か所追加されます)。'
              ),
        ])
      : el(
          'div',
          { class: 'hint' },
          '敷地が未指定です。地図上の「📍 敷地を指定」ボタンから、地図をクリックして敷地の頂点を順番に指定してください。'
        ),
  ]);

  /* ② 建物一覧カード(棟数は通常少なく、項目数は多いため、項目を行・棟を列にした縦横入れ替え表にする) */
  function buildingCell(b: Building, child: HTMLElement | string): HTMLElement {
    const td = el('td', { 'data-id': b.id, onclick: () => selectBuilding(b.id) }, child);
    if (b.id === state.ui.selectedId) td.classList.add('selected');
    return td;
  }
  function buildingInputRow(
    label: string,
    bToAttrs: (b: Building) => Record<string, unknown>
  ): HTMLElement {
    return el('tr', {}, [
      el('th', {}, label),
      ...state.buildings.map((b) =>
        buildingCell(b, el('input', { ...bToAttrs(b), onclick: (e: Event) => e.stopPropagation() }))
      ),
    ]);
  }
  const rebuildAndSave = () => {
    recomputeSlopeCheck();
    recomputeParking();
    redrawBuildingsOverlay();
    renderSidebar();
    scheduleSave();
  };
  const buildingRows = [
    buildingInputRow('棟名', (b) => ({
      type: 'text',
      value: b.name,
      onchange: (e: Event) => {
        b.name = (e.target as HTMLInputElement).value || b.name;
        renderSidebar();
        scheduleSave();
      },
    })),
    buildingInputRow('幅(m)', (b) => ({
      type: 'number',
      value: b.width.toFixed(1),
      step: '0.5',
      min: '2',
      onchange: (e: Event) => {
        b.width = Math.max(2, Number.parseFloat((e.target as HTMLInputElement).value) || b.width);
        rebuildAndSave();
      },
    })),
    buildingInputRow('奥行(m)', (b) => ({
      type: 'number',
      value: b.depth.toFixed(1),
      step: '0.5',
      min: '2',
      onchange: (e: Event) => {
        b.depth = Math.max(2, Number.parseFloat((e.target as HTMLInputElement).value) || b.depth);
        rebuildAndSave();
      },
    })),
    buildingInputRow('角度(°)', (b) => ({
      type: 'number',
      value: Math.round(toDeg(b.angle)),
      step: '5',
      onchange: (e: Event) => {
        b.angle = normalizeAngle(
          toRad(Number.parseFloat((e.target as HTMLInputElement).value) || 0)
        );
        rebuildAndSave();
      },
    })),
    buildingInputRow('階数', (b) => ({
      type: 'number',
      value: b.floors,
      step: '1',
      min: '1',
      onchange: (e: Event) => {
        b.floors = Math.max(
          1,
          Number.parseInt((e.target as HTMLInputElement).value, 10) || b.floors
        );
        rebuildAndSave();
      },
    })),
    buildingInputRow('階高(m)', (b) => ({
      type: 'number',
      value: b.floorHeightM.toFixed(1),
      step: '0.1',
      min: '2',
      onchange: (e: Event) => {
        b.floorHeightM = Math.max(
          2,
          Number.parseFloat((e.target as HTMLInputElement).value) || b.floorHeightM
        );
        rebuildAndSave();
      },
    })),
    el('tr', {}, [
      el('th', {}, '建築面積'),
      ...state.buildings.map((b) => buildingCell(b, (b.width * b.depth).toFixed(0))),
    ]),
    el('tr', {}, [
      el('th', {}, '延床面積'),
      ...state.buildings.map((b) => buildingCell(b, (b.width * b.depth * b.floors).toFixed(0))),
    ]),
    el('tr', {}, [
      el('th', {}, ''),
      ...state.buildings.map((b) =>
        buildingCell(
          b,
          el(
            'button',
            {
              class: 'btn small danger',
              onclick: (e: Event) => {
                e.stopPropagation();
                removeBuilding(b.id);
              },
            },
            '✕'
          )
        )
      ),
    ]),
  ];
  const buildingCard = el('section', { class: 'card' }, [
    el('h2', {}, '② マンション棟'),
    el('div', { class: 'table-scroll' }, [
      el('table', { class: 'tbl', id: 'building-table' }, el('tbody', {}, buildingRows)),
    ]),
    el('div', { class: 'btn-row' }, [
      el('button', { class: 'btn primary small', onclick: addBuilding }, '+ 棟を追加'),
    ]),
    el(
      'div',
      { class: 'hint' },
      '地図上で棟をドラッグして移動、四隅・辺のハンドルでリサイズ、上のオレンジのハンドルで回転できます(Shiftで15°スナップ)。'
    ),
  ]);

  /* ④ 用途地域・法規カード */
  const onSlopeParamChange = () => {
    recomputeSlopeCheck();
    recomputeParking();
    redrawBuildingsOverlay();
    renderSidebar();
    scheduleSave();
  };
  const districtSelect = el(
    'select',
    {
      onchange: (e: Event) => {
        applyZoningPreset(
          state.zoning,
          (e.target as HTMLSelectElement).value as typeof state.zoning.districtType
        );
        onSlopeParamChange();
      },
    },
    Object.entries(ZONING_DISTRICT_PRESETS).map(([key, p]) =>
      el(
        'option',
        { value: key, ...(state.zoning.districtType === key ? { selected: 'selected' } : {}) },
        p.label
      )
    )
  );
  const zoningCard = el('section', { class: 'card' }, [
    el('h2', {}, '④ 用途地域・法規'),
    el('label', {}, '用途地域名(任意)'),
    el('input', {
      type: 'text',
      value: state.zoning.name,
      onchange: (e: Event) => {
        state.zoning.name = (e.target as HTMLInputElement).value;
        scheduleSave();
      },
    }),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '許容建蔽率(%)'),
        el('input', {
          type: 'number',
          value: state.zoning.bcrLimit,
          step: '1',
          onchange: (e: Event) => {
            state.zoning.bcrLimit = Number.parseFloat((e.target as HTMLInputElement).value) || 0;
            updateLegalSummary();
            scheduleSave();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, '許容容積率(%)'),
        el('input', {
          type: 'number',
          value: state.zoning.farLimit,
          step: '10',
          onchange: (e: Event) => {
            state.zoning.farLimit = Number.parseFloat((e.target as HTMLInputElement).value) || 0;
            updateLegalSummary();
            scheduleSave();
          },
        }),
      ]),
    ]),
    el('label', {}, '用途地域区分(斜線制限のプリセット)'),
    districtSelect,
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '道路斜線 勾配'),
        el('input', {
          type: 'number',
          value: state.zoning.road.slopeRatio,
          step: '0.05',
          onchange: (e: Event) => {
            state.zoning.road.slopeRatio =
              Number.parseFloat((e.target as HTMLInputElement).value) || 1.25;
            onSlopeParamChange();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, '適用距離(m)'),
        el('input', {
          type: 'number',
          value: state.zoning.road.applyDistanceM,
          step: '1',
          onchange: (e: Event) => {
            state.zoning.road.applyDistanceM =
              Number.parseFloat((e.target as HTMLInputElement).value) || 20;
            onSlopeParamChange();
          },
        }),
      ]),
    ]),
    el(
      'label',
      { style: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' } },
      [
        el('input', {
          type: 'checkbox',
          ...(state.zoning.adjacent.enabled ? { checked: 'checked' } : {}),
          onchange: (e: Event) => {
            state.zoning.adjacent.enabled = (e.target as HTMLInputElement).checked;
            onSlopeParamChange();
          },
        }),
        '隣地斜線を適用する',
      ]
    ),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '隣地斜線 立上り(m)'),
        el('input', {
          type: 'number',
          value: state.zoning.adjacent.riseM,
          step: '1',
          onchange: (e: Event) => {
            state.zoning.adjacent.riseM =
              Number.parseFloat((e.target as HTMLInputElement).value) || 20;
            onSlopeParamChange();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, '勾配'),
        el('input', {
          type: 'number',
          value: state.zoning.adjacent.slopeRatio,
          step: '0.05',
          onchange: (e: Event) => {
            state.zoning.adjacent.slopeRatio =
              Number.parseFloat((e.target as HTMLInputElement).value) || 1.25;
            onSlopeParamChange();
          },
        }),
      ]),
    ]),
    el(
      'label',
      { style: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' } },
      [
        el('input', {
          type: 'checkbox',
          ...(state.zoning.northSlope.enabled ? { checked: 'checked' } : {}),
          onchange: (e: Event) => {
            state.zoning.northSlope.enabled = (e.target as HTMLInputElement).checked;
            onSlopeParamChange();
          },
        }),
        '北側斜線を適用する',
      ]
    ),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '北側斜線 立上り(m)'),
        el('input', {
          type: 'number',
          value: state.zoning.northSlope.riseM,
          step: '1',
          onchange: (e: Event) => {
            state.zoning.northSlope.riseM =
              Number.parseFloat((e.target as HTMLInputElement).value) || 5;
            onSlopeParamChange();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, '勾配'),
        el('input', {
          type: 'number',
          value: state.zoning.northSlope.slopeRatio,
          step: '0.05',
          onchange: (e: Event) => {
            state.zoning.northSlope.slopeRatio =
              Number.parseFloat((e.target as HTMLInputElement).value) || 1.25;
            onSlopeParamChange();
          },
        }),
      ]),
    ]),
    el(
      'div',
      { class: 'hint' },
      '隣地/北側斜線は、①敷地カードで辺の種別を「隣地」に設定した辺に対して判定されます。'
    ),
  ]);

  /* ⑤ 日影チェック(簡易)カード */
  const HOUR_OPTIONS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  const selBuilding = state.ui.selectedId ? getBuilding(state.ui.selectedId) : null;
  const selBuildingWithHeight =
    selBuilding && 'floors' in selBuilding ? (selBuilding as Building) : null;
  const sunPositions = state.site.origin ? getSunPositions() : new Map();
  const shadowRows = selBuildingWithHeight
    ? state.shadowCheck.hours.map((h) => {
        const sp = sunPositions.get(h);
        const H = getBuildingHeight(selBuildingWithHeight);
        let lenText = '—';
        if (sp?.visible && sp.altitudeDeg >= MIN_SUN_ALTITUDE_DEG && sp.azimuthDeg !== null) {
          lenText = `${(H / Math.tan(toRad(sp.altitudeDeg))).toFixed(1)} m`;
        }
        return el('tr', {}, [
          el('td', {}, `${h}:00`),
          el('td', {}, sp?.visible ? `${sp.altitudeDeg.toFixed(1)}°` : '—(日没)'),
          el(
            'td',
            {},
            sp?.visible && sp.azimuthDeg !== null ? `${sp.azimuthDeg.toFixed(1)}°` : '—'
          ),
          el('td', {}, lenText),
        ]);
      })
    : [];
  const shadowCard = el('section', { class: 'card' }, [
    el('h2', {}, '⑤ 日影チェック(簡易)'),
    el('label', { style: { display: 'flex', alignItems: 'center', gap: '6px' } }, [
      el('input', {
        type: 'checkbox',
        ...(state.shadowCheck.enabled ? { checked: 'checked' } : {}),
        onchange: (e: Event) => {
          state.shadowCheck.enabled = (e.target as HTMLInputElement).checked;
          recomputeSlopeCheck();
          recomputeParking();
          redrawBuildingsOverlay();
          renderSidebar();
          scheduleSave();
        },
      }),
      '地図上に影を表示する',
    ]),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '基準日(月)'),
        el('input', {
          type: 'number',
          value: state.shadowCheck.date.month,
          min: '1',
          max: '12',
          onchange: (e: Event) => {
            state.shadowCheck.date.month = Math.max(
              1,
              Math.min(12, Number.parseInt((e.target as HTMLInputElement).value, 10) || 12)
            );
            invalidateSunPositionCache();
            recomputeSlopeCheck();
            recomputeParking();
            redrawBuildingsOverlay();
            renderSidebar();
            scheduleSave();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, '基準日(日)'),
        el('input', {
          type: 'number',
          value: state.shadowCheck.date.day,
          min: '1',
          max: '31',
          onchange: (e: Event) => {
            state.shadowCheck.date.day = Math.max(
              1,
              Math.min(31, Number.parseInt((e.target as HTMLInputElement).value, 10) || 22)
            );
            invalidateSunPositionCache();
            recomputeSlopeCheck();
            recomputeParking();
            redrawBuildingsOverlay();
            renderSidebar();
            scheduleSave();
          },
        }),
      ]),
    ]),
    el('label', {}, '表示時刻'),
    el(
      'div',
      { style: { display: 'flex', flexWrap: 'wrap', gap: '4px 8px', marginTop: '4px' } },
      HOUR_OPTIONS.map((h) =>
        el(
          'label',
          {
            style: {
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: '10.5px',
              color: 'var(--sub)',
            },
          },
          [
            el('input', {
              type: 'checkbox',
              ...(state.shadowCheck.hours.includes(h) ? { checked: 'checked' } : {}),
              onchange: (e: Event) => {
                if ((e.target as HTMLInputElement).checked) {
                  if (!state.shadowCheck.hours.includes(h)) state.shadowCheck.hours.push(h);
                } else {
                  state.shadowCheck.hours = state.shadowCheck.hours.filter((x) => x !== h);
                }
                state.shadowCheck.hours.sort((a, b) => a - b);
                invalidateSunPositionCache();
                recomputeSlopeCheck();
                recomputeParking();
                redrawBuildingsOverlay();
                renderSidebar();
                scheduleSave();
              },
            }),
            `${h}時`,
            el('span', {
              style: {
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '2px',
                background: shadowColorForHour(h),
              },
            }),
          ]
        )
      )
    ),
    selBuildingWithHeight
      ? el('div', {}, [
          el(
            'div',
            { class: 'hint' },
            `選択中: ${selBuildingWithHeight.name}(高さ ${getBuildingHeight(selBuildingWithHeight).toFixed(1)}m)`
          ),
          el('table', { class: 'tbl' }, [
            el(
              'thead',
              {},
              el(
                'tr',
                {},
                ['時刻', '太陽高度', '方位角', '影の長さ'].map((h) => el('th', {}, h))
              )
            ),
            el('tbody', {}, shadowRows),
          ]),
        ])
      : el(
          'div',
          { class: 'hint' },
          'マンション棟を選択すると、時刻別の太陽高度・方位・影の長さを表示します。'
        ),
    el(
      'div',
      { class: 'hint' },
      'これは簡易的な概算表示です。等時間日影図による法的な適合判定ではありません。正式な判定には専門家による検討が必要です。'
    ),
  ]);

  /* ⑥ 斜線制限チェックカード */
  const slopeRows = state.buildings.map((b) => {
    const sc = slopeCheckCache.get(b.id);
    const mkCell = (res: { applicable: boolean; ok: boolean; overM: number } | undefined) => {
      if (!res) return el('td', {}, '—');
      if (!res.applicable)
        return el(
          'td',
          {},
          el(
            'span',
            { class: 'badge', style: { background: '#e5e7eb', color: '#6b7280' } },
            '対象外'
          )
        );
      return el(
        'td',
        {},
        el(
          'span',
          { class: `badge ${res.ok ? 'ok' : 'danger'}` },
          res.ok ? 'OK' : `NG(+${res.overM.toFixed(1)}m)`
        )
      );
    };
    return el('tr', {}, [
      el('td', {}, b.name),
      el('td', {}, sc ? `${sc.heightM.toFixed(1)}m` : '—'),
      mkCell(sc?.road),
      mkCell(sc?.adjacent),
      mkCell(sc?.north),
    ]);
  });
  const slopeCard = el('section', { class: 'card' }, [
    el('h2', {}, '⑥ 斜線制限チェック'),
    state.buildings.length > 0
      ? el('table', { class: 'tbl' }, [
          el(
            'thead',
            {},
            el(
              'tr',
              {},
              ['棟名', '高さ', '道路斜線', '隣地斜線', '北側斜線'].map((h) => el('th', {}, h))
            )
          ),
          el('tbody', {}, slopeRows),
        ])
      : el(
          'div',
          { class: 'hint' },
          'マンション棟を追加すると斜線制限のチェック結果が表示されます。'
        ),
    el(
      'div',
      { class: 'hint' },
      'NGの建物は地図上で枠が赤く強調表示されます。判定は①敷地カードの辺属性と④用途地域パラメータに基づく簡易計算です。'
    ),
  ]);

  /* ③ 駐車場・駐輪場カード */
  const parkingBadge = (capacity: number, required: number | null) => {
    if (required == null || required <= 0)
      return el(
        'span',
        { class: 'badge', style: { background: '#e5e7eb', color: '#6b7280' } },
        '未設定'
      );
    return el(
      'span',
      { class: `badge ${capacity >= required ? 'ok' : 'danger'}` },
      capacity >= required ? 'OK' : '不足'
    );
  };
  const onParkingParamChange = () => {
    recomputeParking();
    redrawBuildingsOverlay();
    renderSidebar();
    scheduleSave();
  };
  const parkingCard = el('section', { class: 'card' }, [
    el('h2', {}, '③ 駐車場・駐輪場'),
    !state.site.locked
      ? el('div', { class: 'hint' }, '敷地を指定すると、地図上に駐車場・駐輪場が自動配置されます。')
      : el('div', {}),
    el('label', { style: { fontWeight: 700, color: 'var(--text)' } }, '駐車場'),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '必要台数'),
        el('input', {
          type: 'number',
          value: state.parking.requiredCars ?? '',
          min: '0',
          placeholder: '未設定',
          onchange: (e: Event) => {
            const v = (e.target as HTMLInputElement).value;
            state.parking.requiredCars = v === '' ? null : Math.max(0, Number.parseInt(v, 10) || 0);
            onParkingParamChange();
          },
        }),
      ]),
      el('div', { style: { display: 'flex', alignItems: 'flex-end' } }, [
        el(
          'button',
          {
            class: 'btn small',
            onclick: () => {
              const totalGfa = computeLegalCheck(state).totalGfa;
              const units = totalGfa / (state.parking.assumedUnitArea || 70);
              state.parking.requiredCars = Math.ceil(units * state.parking.ratioPerUnit);
              onParkingParamChange();
            },
          },
          '延床面積から概算'
        ),
      ]),
      el('div', {}, [
        el('label', {}, '確保目標比率'),
        el(
          'select',
          {
            onchange: (e: Event) => {
              state.parking.targetRatio = Number.parseFloat((e.target as HTMLSelectElement).value);
              onParkingParamChange();
            },
          },
          PARKING_TARGET_RATIOS.map((r) =>
            el(
              'option',
              { value: r, ...(state.parking.targetRatio === r ? { selected: 'selected' } : {}) },
              `${Math.round(r * 100)}%`
            )
          )
        ),
      ]),
    ]),
    el('div', { class: 'kpi-line' }, [
      el('span', {}, '確保台数 / 確保目標'),
      el('span', {}, [
        el('b', {}, `${state.parking.capacity} / ${computeParkingTarget(state.parking) ?? '—'}`),
        ' ',
        parkingBadge(state.parking.capacity, computeParkingTarget(state.parking)),
      ]),
    ]),
    state.parking.requiredCars != null && state.parking.targetRatio !== 1
      ? el(
          'div',
          { class: 'hint' },
          `必要台数 ${state.parking.requiredCars}台 × ${Math.round(state.parking.targetRatio * 100)}% ＝ 確保目標 ${computeParkingTarget(state.parking)}台。`
        )
      : el('div', {}),
    state.parking.circulationWarning && state.parking.circulationWarning.disconnectedCount > 0
      ? el(
          'div',
          { class: 'hint', style: { color: 'var(--danger)' } },
          `⚠ 約${state.parking.circulationWarning.disconnectedCount}台は、他の通路を経由しても出入口まで動線がつながっていない可能性があります(地図上の赤い点線の通路)。出入口の追加や配置方式・領域の調整をご検討ください。`
        )
      : el('div', {}),
    ...(EXTRA_PARKING_METHODS_ENABLED
      ? [
          !state.parking.methods.tandem.enabled && !state.parking.methods.roadDirect.enabled
            ? el(
                'div',
                { class: 'hint' },
                '確保台数(背合わせ)のみで確保目標に達しているため、追加の配置方式は現在使用していません。'
              )
            : el('label', { style: { marginTop: '8px' } }, '確保目標のため自動追加された配置方式'),
          (() => {
            const key = 'tandem' as const;
            const m = state.parking.methods[key];
            const color = PARKING_METHOD_COLORS[key];
            const header = el(
              'div',
              { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
              [
                el('span', {
                  style: {
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '2px',
                    background: color,
                    flex: 'none',
                  },
                }),
                `${PARKING_METHOD_LABELS[key]}${m.enabled ? '' : '(現在未使用)'}`,
              ]
            );
            const detail = [
              m.enabled
                ? el('div', { class: 'kpi-line' }, [
                    el('span', {}, '配置数'),
                    el('b', {}, `${m.capacity || 0} 台`),
                  ])
                : el('div', {}),
              el('div', { class: 'btn-row' }, [
                el(
                  'button',
                  {
                    class: 'btn small',
                    onclick: () => {
                      m.zone = null;
                      onParkingParamChange();
                    },
                  },
                  '領域をリセット'
                ),
              ]),
            ];
            return el(
              'div',
              {
                style: {
                  marginBottom: '10px',
                  paddingLeft: '8px',
                  borderLeft: `3px solid ${color}`,
                },
              },
              [header, el('div', { style: { paddingLeft: '16px' } }, detail)]
            );
          })(),
          (() => {
            const key = 'roadDirect' as const;
            const m = state.parking.methods[key];
            const color = PARKING_METHOD_COLORS[key];
            const header = el(
              'div',
              { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
              [
                el('span', {
                  style: {
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '2px',
                    background: color,
                    flex: 'none',
                  },
                }),
                `${PARKING_METHOD_LABELS[key]}(通路なしで道路に垂直に配置。狭い敷地向け)${m.enabled ? '' : '(現在未使用)'}`,
              ]
            );
            const roadEdgeList = state.site.edges
              .map((edge, i) => ({ edge, i }))
              .filter(({ edge }) => edge.type === 'road');
            const detail = [
              m.enabled
                ? el('div', { class: 'kpi-line' }, [
                    el('span', {}, '配置数'),
                    el('b', {}, `${m.capacity || 0} 台`),
                  ])
                : el('div', {}),
              el('label', {}, '対象辺(未選択=道路の全辺)'),
              el(
                'div',
                { style: { display: 'flex', flexWrap: 'wrap', gap: '4px 10px' } },
                roadEdgeList.map(({ i }) => {
                  const checked =
                    !Array.isArray(state.parking.roadDirectEdgeIndices) ||
                    state.parking.roadDirectEdgeIndices.includes(i);
                  return el(
                    'label',
                    {
                      style: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        fontSize: '11px',
                        color: 'var(--sub)',
                      },
                    },
                    [
                      el('input', {
                        type: 'checkbox',
                        ...(checked ? { checked: 'checked' } : {}),
                        onchange: (e: Event) => {
                          const roadIdx = roadEdgeList.map(({ i: idx }) => idx);
                          let cur = Array.isArray(state.parking.roadDirectEdgeIndices)
                            ? state.parking.roadDirectEdgeIndices.slice()
                            : roadIdx.slice();
                          if ((e.target as HTMLInputElement).checked) {
                            if (!cur.includes(i)) cur.push(i);
                          } else {
                            cur = cur.filter((x) => x !== i);
                          }
                          state.parking.roadDirectEdgeIndices =
                            cur.length === roadIdx.length ? null : cur;
                          onParkingParamChange();
                        },
                      }),
                      `辺${i + 1}`,
                    ]
                  );
                })
              ),
              roadEdgeList.length === 0
                ? el(
                    'div',
                    { class: 'hint' },
                    '①敷地カードで辺の種別を「道路」に設定してください。'
                  )
                : el('div', {}),
              el('div', { class: 'btn-row' }, [
                el(
                  'button',
                  {
                    class: 'btn small',
                    onclick: () => {
                      m.zone = null;
                      onParkingParamChange();
                    },
                  },
                  '領域をリセット'
                ),
              ]),
            ];
            return el(
              'div',
              {
                style: {
                  marginBottom: '10px',
                  paddingLeft: '8px',
                  borderLeft: `3px solid ${color}`,
                },
              },
              [header, el('div', { style: { paddingLeft: '16px' } }, detail)]
            );
          })(),
        ]
      : []),
    el(
      'div',
      { class: 'hint' },
      EXTRA_PARKING_METHODS_ENABLED
        ? '背合わせは敷地全体(建物を除く)を対象に自動配置されます。確保台数が確保目標に届かない場合、縦列(タンデム)・道路直接駐車の順で自動的に有効化され、届き次第それ以上は追加されません。地図上の点線ゾーンはドラッグ・ハンドルで移動/リサイズ/回転して領域を指定できます。'
        : '背合わせ(壁背負いを含む)は敷地全体(建物を除く)を対象に自動配置されます。'
    ),
    el('div', {}, [
      el(
        'label',
        { style: { marginTop: '8px' } },
        EXTRA_PARKING_METHODS_ENABLED ? '角度・向きの設定(背合わせ/縦列で共通)' : '角度・向きの設定'
      ),
      el('div', { class: 'row' }, [
        el('div', {}, [
          el('label', {}, '配置角度モード'),
          el(
            'select',
            {
              onchange: (e: Event) => {
                state.parking.angleMode = (e.target as HTMLSelectElement)
                  .value as typeof state.parking.angleMode;
                onParkingParamChange();
              },
            },
            [
              el(
                'option',
                {
                  value: 'auto',
                  ...(state.parking.angleMode === 'auto' ? { selected: 'selected' } : {}),
                },
                '自動(台数最大の角度を探索)'
              ),
              el(
                'option',
                {
                  value: 'manual',
                  ...(state.parking.angleMode === 'manual' ? { selected: 'selected' } : {}),
                },
                '手動固定'
              ),
            ]
          ),
        ]),
        state.parking.angleMode === 'manual'
          ? el('div', {}, [
              el('label', {}, '固定角度(敷地主軸からの相対角)'),
              el(
                'select',
                {
                  onchange: (e: Event) => {
                    state.parking.manualAngleDeg = Number.parseFloat(
                      (e.target as HTMLSelectElement).value
                    );
                    onParkingParamChange();
                  },
                },
                PARKING_ANGLE_OFFSETS_DEG.map((d) =>
                  el(
                    'option',
                    {
                      value: d,
                      ...(state.parking.manualAngleDeg === d ? { selected: 'selected' } : {}),
                    },
                    `${d}°`
                  )
                )
              ),
            ])
          : el('div', {}),
      ]),
      el('label', {}, 'マスの並び方向(通路の向き)'),
      el(
        'select',
        {
          onchange: (e: Event) => {
            state.parking.rowsAlongXMode = (e.target as HTMLSelectElement)
              .value as typeof state.parking.rowsAlongXMode;
            onParkingParamChange();
          },
        },
        [
          el(
            'option',
            {
              value: 'auto',
              ...(state.parking.rowsAlongXMode === 'auto' ? { selected: 'selected' } : {}),
            },
            '自動(台数最大の向きを選択)'
          ),
          el(
            'option',
            {
              value: 'rows',
              ...(state.parking.rowsAlongXMode === 'rows' ? { selected: 'selected' } : {}),
            },
            'パターンA固定'
          ),
          el(
            'option',
            {
              value: 'cols',
              ...(state.parking.rowsAlongXMode === 'cols' ? { selected: 'selected' } : {}),
            },
            'パターンB固定'
          ),
        ]
      ),
      el(
        'div',
        { class: 'hint' },
        '「自動」の場合、台数が同程度でもパターンA/Bどちらが選ばれるかは敷地形状に依存します。向きを固定すると、常に同じパターンで配置されます。'
      ),
    ]),
    el('label', { style: { marginTop: '8px' } }, '詳細設定'),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, 'マス幅(m)'),
        el('input', {
          type: 'number',
          value: state.parking.stallW,
          step: '0.1',
          onchange: (e: Event) => {
            state.parking.stallW = Number.parseFloat((e.target as HTMLInputElement).value) || 2.5;
            onParkingParamChange();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, 'マス奥行(m)'),
        el('input', {
          type: 'number',
          value: state.parking.stallD,
          step: '0.1',
          onchange: (e: Event) => {
            state.parking.stallD = Number.parseFloat((e.target as HTMLInputElement).value) || 5.0;
            onParkingParamChange();
          },
        }),
      ]),
    ]),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '通路幅'),
        el('div', { class: 'kpi-line', style: { padding: '4px 0' } }, [
          el('b', {}, `${AISLE_WIDTH_FIXED_M.toFixed(1)}m`),
          el('span', { class: 'hint' }, '(固定)'),
        ]),
      ]),
      el('div', {}, [
        el('label', {}, '敷地境界余裕(m)'),
        el('input', {
          type: 'number',
          value: state.parking.siteClearance,
          step: '0.1',
          onchange: (e: Event) => {
            state.parking.siteClearance =
              Number.parseFloat((e.target as HTMLInputElement).value) || 0;
            onParkingParamChange();
          },
        }),
      ]),
    ]),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', {}, '身障者用(台)'),
        el('input', {
          type: 'number',
          value: state.parking.disabledCount,
          min: '0',
          onchange: (e: Event) => {
            state.parking.disabledCount = Math.max(
              0,
              Number.parseInt((e.target as HTMLInputElement).value, 10) || 0
            );
            onParkingParamChange();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, '来客用(台)'),
        el('input', {
          type: 'number',
          value: state.parking.guestCount,
          min: '0',
          onchange: (e: Event) => {
            state.parking.guestCount = Math.max(
              0,
              Number.parseInt((e.target as HTMLInputElement).value, 10) || 0
            );
            onParkingParamChange();
          },
        }),
      ]),
      el('div', {}, [
        el('label', {}, 'EV用(台)'),
        el('input', {
          type: 'number',
          value: state.parking.evCount,
          min: '0',
          onchange: (e: Event) => {
            state.parking.evCount = Math.max(
              0,
              Number.parseInt((e.target as HTMLInputElement).value, 10) || 0
            );
            onParkingParamChange();
          },
        }),
      ]),
    ]),
    el('label', { style: { fontWeight: 700, color: 'var(--text)', marginTop: '12px' } }, '駐輪場'),
    el('label', { style: { display: 'flex', alignItems: 'center', gap: '6px' } }, [
      el('input', {
        type: 'checkbox',
        ...(state.bicycle.enabled ? { checked: 'checked' } : {}),
        onchange: (e: Event) => {
          state.bicycle.enabled = (e.target as HTMLInputElement).checked;
          onParkingParamChange();
        },
      }),
      '駐輪場を有効にする',
    ]),
    state.bicycle.enabled
      ? el('div', {}, [
          el('div', { class: 'row' }, [
            el('div', {}, [
              el('label', {}, '必要台数'),
              el('input', {
                type: 'number',
                value: state.bicycle.requiredCount ?? '',
                min: '0',
                placeholder: '未設定',
                onchange: (e: Event) => {
                  const v = (e.target as HTMLInputElement).value;
                  state.bicycle.requiredCount =
                    v === '' ? null : Math.max(0, Number.parseInt(v, 10) || 0);
                  onParkingParamChange();
                },
              }),
            ]),
            el('div', { style: { display: 'flex', alignItems: 'flex-end' } }, [
              el(
                'button',
                {
                  class: 'btn small',
                  onclick: () => {
                    const totalGfa = computeLegalCheck(state).totalGfa;
                    const units = totalGfa / (state.bicycle.assumedUnitArea || 70);
                    state.bicycle.requiredCount = Math.ceil(units * state.bicycle.ratioPerUnit);
                    onParkingParamChange();
                  },
                },
                '延床面積から概算'
              ),
            ]),
          ]),
          el('div', {}, [
            el('label', {}, '1台あたり面積(m²)'),
            el('input', {
              type: 'number',
              value: state.bicycle.unitArea,
              step: '0.1',
              onchange: (e: Event) => {
                state.bicycle.unitArea =
                  Number.parseFloat((e.target as HTMLInputElement).value) || 0.6;
                onParkingParamChange();
              },
            }),
          ]),
          el('div', { class: 'kpi-line' }, [
            el('span', {}, '範囲'),
            el(
              'span',
              { class: 'hint', style: { display: 'inline' } },
              state.bicycle.autoZone ? '自動検出' : '手動編集済み'
            ),
          ]),
          !state.bicycle.autoZone
            ? el('div', { class: 'btn-row' }, [
                el(
                  'button',
                  {
                    class: 'btn small',
                    onclick: () => {
                      state.bicycle.autoZone = true;
                      onParkingParamChange();
                    },
                  },
                  '自動配置に戻す'
                ),
              ])
            : el('div', {}),
          el('div', { class: 'kpi-line' }, [
            el('span', {}, '収容可能台数 / 必要台数'),
            el('span', {}, [
              el('b', {}, `${state.bicycle.capacity} / ${state.bicycle.requiredCount ?? '—'}`),
              ' ',
              parkingBadge(state.bicycle.capacity, state.bicycle.requiredCount),
            ]),
          ]),
          el(
            'div',
            { class: 'hint' },
            '地図上で駐輪場エリア(ピンクの点線)をドラッグ・ハンドルで移動/リサイズ/回転すると、その範囲が手動指定として保持されます。'
          ),
        ])
      : el(
          'div',
          { class: 'hint' },
          '駐輪場を有効にすると、残地に自動でエリアが配置されます(地図上で手動調整も可能)。'
        ),
    el(
      'div',
      { class: 'hint' },
      '配置方式ごとに指定した領域内で、グリッド近似により駐車マスを自動配置する簡易機能です(角度0/30/45/60/90度を自動で試算)。出入口は①敷地カードで設定できます。実際のレイアウトは出入口・通路計画等を踏まえ設計者が確定してください。'
    ),
  ]);

  /* ⑦ 集計結果カード */
  const summaryCard = el('section', { class: 'card' }, [
    el('h2', {}, '⑦ 集計結果'),
    el('div', { class: 'kpi-line' }, [
      el('span', {}, '敷地面積'),
      el('b', { id: 'sum-site-area' }, `${r.siteArea.toFixed(1)} m²`),
    ]),
    el('div', { class: 'kpi-line' }, [
      el('span', {}, '建築面積合計'),
      el('b', { id: 'sum-footprint' }, `${r.totalFootprint.toFixed(1)} m²`),
    ]),
    el('div', { class: 'kpi-line' }, [
      el('span', {}, '建蔽率'),
      el('span', {}, [
        el('b', { id: 'sum-bcr' }, r.bcr.toFixed(1)),
        '% ',
        el(
          'span',
          { class: `badge ${r.bcrOk ? 'ok' : 'danger'}`, id: 'badge-bcr' },
          r.bcrOk ? 'OK' : 'NG'
        ),
      ]),
    ]),
    el('div', { class: 'kpi-line' }, [
      el('span', {}, '延床面積合計'),
      el('b', { id: 'sum-gfa' }, `${r.totalGfa.toFixed(1)} m²`),
    ]),
    el('div', { class: 'kpi-line' }, [
      el('span', {}, '容積率'),
      el('span', {}, [
        el('b', { id: 'sum-far' }, r.far.toFixed(1)),
        '% ',
        el(
          'span',
          { class: `badge ${r.farOk ? 'ok' : 'danger'}`, id: 'badge-far' },
          r.farOk ? 'OK' : 'NG'
        ),
      ]),
    ]),
  ]);

  sidebar.append(
    makeCollapsible(siteCard, 'site', renderSidebar),
    makeCollapsible(buildingCard, 'building', renderSidebar),
    makeCollapsible(parkingCard, 'parking', renderSidebar),
    makeCollapsible(zoningCard, 'zoning', renderSidebar),
    makeCollapsible(shadowCard, 'shadow', renderSidebar),
    makeCollapsible(slopeCard, 'slope', renderSidebar),
    makeCollapsible(summaryCard, 'summary', renderSidebar)
  );
}
